import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import useSWRMutation from 'swr/mutation'
import { DetectionModel } from 'types'

interface CreateConfigurationRequest {
  name?: string
  target_sensor_entry_id: string
  polling_frequency_minutes: number
  detection_models: DetectionModel[]
  is_enabled?: boolean
  overwrite?: boolean
}

interface UpdateConfigurationRequest {
  name?: string
  polling_frequency_minutes?: number
  detection_models?: DetectionModel[]
  is_enabled?: boolean
}

interface ConfigurationResponse {
  id: string
  target_sensor_entry_id: string
  polling_frequency_minutes: number
  detection_models: DetectionModel[]
  is_enabled: boolean
  created_at: string
  updated_at: string
}

export const useDetectionConfigurationMutation = () => {
  const { authorizedAxios } = useGeckoAuth()
  const { ANOMALY_DETECTION_SERVICE_URL } = useRuntimeVariables()

  const postFetcher = async (
    url: string,
    { arg }: { arg: CreateConfigurationRequest }
  ): Promise<ConfigurationResponse> => {
    const response = await authorizedAxios.post(url, arg)
    return response.data
  }

  const patchFetcher = async (
    url: string,
    {
      arg,
    }: { arg: { configurationId: string; data: UpdateConfigurationRequest } }
  ): Promise<ConfigurationResponse> => {
    const response = await authorizedAxios.patch(
      `${url}/${arg.configurationId}`,
      arg.data
    )
    return response.data
  }

  const deleteFetcher = async (
    url: string,
    { arg }: { arg: { configurationId: string } }
  ): Promise<void> => {
    await authorizedAxios.delete(`${url}/${arg.configurationId}`)
  }

  const postMutation = useSWRMutation<
    ConfigurationResponse,
    Error,
    string,
    CreateConfigurationRequest
  >(`${ANOMALY_DETECTION_SERVICE_URL}/api/v1/configurations`, postFetcher)

  const patchMutation = useSWRMutation<
    ConfigurationResponse,
    Error,
    string,
    { configurationId: string; data: UpdateConfigurationRequest }
  >(`${ANOMALY_DETECTION_SERVICE_URL}/api/v1/configurations`, patchFetcher)

  const deleteMutation = useSWRMutation<
    void,
    Error,
    string,
    { configurationId: string }
  >(`${ANOMALY_DETECTION_SERVICE_URL}/api/v1/configurations`, deleteFetcher)

  return {
    post: postMutation.trigger,
    patch: patchMutation.trigger,
    delete: deleteMutation.trigger,
    isLoading:
      postMutation.isMutating ||
      patchMutation.isMutating ||
      deleteMutation.isMutating,
    error: postMutation.error || patchMutation.error || deleteMutation.error,
  }
}
